import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import { Typography, Box } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import XIcon from "@material-ui/icons/Clear";
import clsx from "clsx";
import { PATHS } from "util/appConstants";
import { selectTour, selectTourStatus, getTour } from "redux/slices/tourSlice";
import { getAccountPermissions, selectAccountPermission } from "redux/slices/accountPermissionsSlice";
import Navbar from "components/Navbar";
import CustomersNavbar from "components/Masterbar/CustomersBar";
import DarkLayout from "components/Shared/DarkLayout";
import { selectUser } from "redux/slices/userSlice";
import { sortOrder } from "util/customers/deliveryTimings";
import { getSupplierLocations, selectSupplierLocations } from "redux/slices/supplierLocationsSlice";
import TourRouteVisualization from "components/Tours/TourRouteVisualization";
import { getTourMapColor } from "util/tourMapColor";
import { getTourDeliveryTimings } from "util/helpers";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _infoheading: {
    color: "#F5F5F5",
    font: "normal normal medium 22px/32px Roboto",
    marginTop: "44px",
  },
  _head: {
    color: "#F5F5F5",
    opacity: "0.6",
    font: "normal normal normal 12px/24px Roboto",
    marginRight: "32px",
  },
  _basicdetailhead: {
    display: "flex",
    marginTop: "24px",
    flexWrap: "wrap",
    gap: "32px",
  },
  _basicdetail: {
    display: "flex",
    flexWrap: "wrap",
    gap: "32px",
  },
  _detail: {
    color: "#F5F5F5",
    font: "normal normal normal 18px/24px Roboto",
  },
  _name: {
    letterSpacing: "0px",
  },
  _width11: {},
  _margintop80: {
    marginTop: "40px",
  },
  _adressbox: {
    marginTop: "24px",
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
  _timingHeading: {
    color: '#6F9CEB',
    fontWeight: 'bold',
    marginTop: '2.5rem',
    marginBottom: '0px',
    fontSize: '18px',
  },
  _routeVisualizationBox: {
    marginTop: '40px',
    marginBottom: '7rem',
  },
});

const TourDetail = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const loading = useSelector(selectTourStatus);
  const tour = useSelector(selectTour);
  const user = useSelector(selectUser);
  const accountPermissions = useSelector(selectAccountPermission);
  const supplierLocations = useSelector(selectSupplierLocations);

  useEffect(() => {
    if (id && !loading) {
      dispatch(getTour(id));
    }
    dispatch(getSupplierLocations());
    dispatch(getAccountPermissions());
  }, [dispatch, id]);

  const editTourHandler = () => {
    history.push(PATHS.tours.edit.replace(":id", id));
  };

  const getEndPointLocation = () => {
    if (!tour || !supplierLocations) return null;
    if (tour.tour_type === "return_to_origin") {
      return supplierLocations.find((location) => tour.supplier_location_id === location.id)?.name;
    }
    const endPointStop = tour.stops?.find((s) => s.type === "end_point");
    return endPointStop
      ? supplierLocations.find((location) => endPointStop.supplier_location_id === location.id)?.name
      : null;
  };

  const getStopsBeforeEnd = () => {
    if (!tour || !supplierLocations) return [];
    const beforeEndStops = tour.stops?.filter((s) => s.type === "before_end") || [];
    return beforeEndStops
      .map((stop) => supplierLocations.find((location) => stop.supplier_location_id === location.id)?.name)
      .filter(Boolean);
  };

  const getStopsAfterEnd = () => {
    if (!tour || !supplierLocations) return [];
    const afterEndStops = tour.stops?.filter((s) => s.type === "after_end") || [];
    return afterEndStops
      .map((stop) => supplierLocations.find((location) => stop.supplier_location_id === location.id)?.name)
      .filter(Boolean);
  };

  const getSupplierLocationName = (locationId) =>
    supplierLocations?.find((location) => location.id === locationId)?.name || locationId;

  const normalizePickupStops = (pickupStops) => {
    if (!Array.isArray(pickupStops)) return [];
    return [...pickupStops]
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .map((stop) => stop?.supplier_location_id)
      .filter(Boolean);
  };

  const getPickupRoutes = () => {
    if (!tour) return [];
    let timingConfigs = tour.delivery_timing_configs;

    if (typeof timingConfigs === "string") {
      try {
        timingConfigs = JSON.parse(timingConfigs);
      } catch (err) {
        timingConfigs = [];
      }
    }

    if (Array.isArray(timingConfigs) && timingConfigs.length > 0) {
      const routes = timingConfigs
        .map((config) => {
          let pickupStops = config?.pickup_stops;
          if (typeof pickupStops === "string") {
            try {
              pickupStops = JSON.parse(pickupStops);
            } catch (err) {
              pickupStops = [];
            }
          }
          return {
            delivery_timing: config.delivery_timing,
            pickup_start: config.pickup_start || null,
            pickup_end: config.pickup_end || null,
            stops: normalizePickupStops(pickupStops),
          };
        })
        .filter((route) => route.pickup_start || route.pickup_end || route.stops.length > 0);

      if (routes.length > 0) {
        return routes;
      }
    }

    let pickupRoutes = tour.pickup_routes;

    if (typeof pickupRoutes === "string") {
      try {
        pickupRoutes = JSON.parse(pickupRoutes);
      } catch (err) {
        pickupRoutes = [];
      }
    }

    if (Array.isArray(pickupRoutes) && pickupRoutes.length > 0) {
      return pickupRoutes.map((route) => ({
        delivery_timing: route.delivery_timing,
        pickup_start: route.pickup_start || null,
        pickup_end: route.pickup_end || null,
        stops: Array.isArray(route.stops) ? route.stops : [],
      }));
    }

    if (tour.pickup_start || tour.pickup_end) {
      const timings = getTourDeliveryTimings(tour);
      return timings.map((timing) => ({
        delivery_timing: timing,
        pickup_start: tour.pickup_start || null,
        pickup_end: tour.pickup_end || null,
        stops: Array.isArray(tour.pickup_route_stops) ? tour.pickup_route_stops : [],
      }));
    }

    return [];
  };

  // For custom routes per timing
  const getEndPointLocationForTiming = (config) => {
    if (!config || !supplierLocations) return null;
    if (config.tour_type === "return_to_origin") {
      return supplierLocations.find((location) => config.supplier_location_id === location.id)?.name;
    }
    if (config.route_end) {
      return supplierLocations.find((location) => config.route_end === location.id)?.name;
    }
    return null;
  };

  const getStopsBeforeEndForTiming = (config) => {
    if (!config || !supplierLocations || !config.fix_stops) return [];
    const beforeEndStops = config.fix_stops.filter((s) => s.type === "before_end") || [];
    return beforeEndStops
      .map((stop) => supplierLocations.find((location) => stop.supplier_location_id === location.id)?.name)
      .filter(Boolean);
  };

  const getStopsAfterEndForTiming = (config) => {
    if (!config || !supplierLocations || !config.fix_stops) return [];
    const afterEndStops = config.fix_stops.filter((s) => s.type === "after_end") || [];
    return afterEndStops
      .map((stop) => supplierLocations.find((location) => stop.supplier_location_id === location.id)?.name)
      .filter(Boolean);
  };

  const pickupRoutes = getPickupRoutes();
  const tourTypeCategory = tour?.tour_type_category ?? tour?.tour_category;
  const tourColor =
    typeof tour?.color === "string" && tour.color.trim()
      ? tour.color.trim()
      : getTourMapColor(tour?.id, tour?.name);
  const isPickupOnlyTour =
    tourTypeCategory === "pickup" || (!tourTypeCategory && pickupRoutes.length > 0);
  const isMixTour = tourTypeCategory === "mix";
  const isDeliveryTour = tourTypeCategory === "delivery";
  const showPickupDetails = pickupRoutes.length > 0 || isPickupOnlyTour || isMixTour;
  const deliverySetupGridColumns = isDeliveryTour
    ? "1.2fr 0.55fr 0.9fr 0.7fr 1fr 1fr 1fr"
    : "1fr 1fr 1fr 1fr";
  const deliveryStartLabel = isMixTour ? t("Pickup End / Delivery Start") : t("Tour Start Location");
  const pickupEndLabel = isMixTour ? t("Pickup End / Delivery Start") : t("Pickup End");

  return (
    <>
      <Navbar />
      <CustomersNavbar />
      <DarkLayout doublebar loading={loading || !tour}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {tour?.name}
          </Typography>
          {(user?.permissions?.toursCreate || user?.permissions?.tours?.create) && (
            <Box className={classes._buttonbox} component="div">
              <EditIcon onClick={editTourHandler} className={classes._edit} />
              <Typography component="span" className={clsx(classes._edittext, "edittag")}>
                {t("Edit")}
              </Typography>
            </Box>
          )}
        </div>
        <Typography className={classes._infoheading} variant="h5">
          {t("Tour Details")}
        </Typography>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={clsx(classes._head, classes._width11)} variant="h6">
              {t("Tour ID")}
            </Typography>
            <Typography className={clsx(classes._detail, classes._width11)} variant="h6">
              {tour?.user_defined_tour_id}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._width11)} variant="h6">
              {t("Tour Type")}
            </Typography>
            <Typography className={clsx(classes._detail, classes._width11)} variant="h6">
              {isPickupOnlyTour
                ? t("Pickup Tour")
                : isMixTour
                  ? t("Mix Tour")
                  : tourTypeCategory === "delivery"
                    ? t("Delivery Tour")
                    : tourTypeCategory
                      ? t(tourTypeCategory)
                      : "-"}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Name")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {tour?.name}
            </Typography>
          </div>
          <div>
            <Typography className={classes._head} variant="h6">
              {t("Description")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {tour?.description}
            </Typography>
          </div>
          <div>
            <Typography className={classes._head} variant="h6">
              {t("Active")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {tour?.active ? <CheckIcon /> : <XIcon />}
            </Typography>
          </div>
          {!isPickupOnlyTour && (
            <>
              <div>
                <Typography className={classes._head} variant="h6">
                  {t("Default Tour")}
                </Typography>
                <Typography className={classes._detail} variant="h6">
                  {tour?.is_default ? <CheckIcon /> : <XIcon />}
                </Typography>
              </div>
              {tourTypeCategory === "delivery" && (
                <div>
                  <Typography className={classes._head} variant="h6">
                    {t("Handover type")}
                  </Typography>
                  <Typography className={classes._detail} variant="h6">
                    {tour?.middle_mile_digital ? t("Digitalised") : t("Paper based")}
                  </Typography>
                </div>
              )}
              <div>
                <Typography className={classes._head} variant="h6">
                  {t("Third party")}
                </Typography>
                <Typography className={classes._detail} variant="h6">
                  {tour?.third_party ? <CheckIcon /> : <XIcon />}
                </Typography>
              </div>
            </>
          )}
          {!isPickupOnlyTour && (
            <div>
              <Typography className={classes._head} variant="h6">
                {t("Tour Color")}
              </Typography>
              <Typography className={classes._detail} variant="h6" component="div">
                <span
                  title={tourColor}
                  style={{
                    display: "inline-block",
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    backgroundColor: tourColor,
                    verticalAlign: "middle",
                  }}
                />
              </Typography>
            </div>
          )}
        </div>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={classes._head} variant="h6">
              {t("Delivery Timings")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {(() => {
                const timings = getTourDeliveryTimings(tour);
                return timings.length
                  ? [...timings]
                    .sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b))
                    .map((deliveryTiming) => t(deliveryTiming))
                    .join(", ")
                  : "";
              })()}
            </Typography>
          </div>
        </div>

        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={classes._head} variant="h6">
              {t("Allowed Supplier Locations")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {supplierLocations
                ?.filter((location) =>
                  tour?.ToursAlloweds?.some((allowed) => allowed?.supplier_location_id === location.id)
                )
                .map((location) => location.name)
                .join(", ")}
            </Typography>
          </div>
        </div>
        {tour?.delivery_timing_configs && tour.delivery_timing_configs.length > 0 ? (
          <>
            <Typography className={classes._infoheading} variant="h5">
              {isPickupOnlyTour ? t("Pickup Route Setup") : t("Delivery Timing Permissions")}
            </Typography>
            <div style={{ marginTop: "16px" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: deliverySetupGridColumns,
                gap: "16px",
                marginBottom: "12px",
                fontWeight: "bold",
                color: "white"
              }}>
                <div>{t("Delivery Timing")}</div>
                {isDeliveryTour && (
                  <div style={{ textAlign: "center" }}>{t("Start Time")}</div>
                )}
                {isDeliveryTour && (
                  <div style={{ textAlign: "center" }}>{t("Time Constraint")}</div>
                )}
                {isDeliveryTour && (
                  <div style={{ textAlign: "center" }}>{t("Vehicles")}</div>
                )}
                <div style={{ textAlign: "center" }}>{t("Scan Code Enabled")}</div>
                <div style={{ textAlign: "center" }}>{t("Allow Manual Orders Marking")}</div>
                <div style={{ textAlign: "center" }}>{t("Bypass Route Start Verification")}</div>

              </div>

              {[...tour.delivery_timing_configs]
                .sort((a, b) => sortOrder.indexOf(a.delivery_timing) - sortOrder.indexOf(b.delivery_timing))
                .map((config) => (
                  <div key={config.delivery_timing} style={{
                    display: "grid",
                    gridTemplateColumns: deliverySetupGridColumns,
                    gap: "16px",
                    padding: "8px 0",
                    borderBottom: "1px solid #e0e0e0",
                    alignItems: "center",
                    color: "white"
                  }}>
                    <div style={{ fontWeight: "bold" }}>
                      {t(config.delivery_timing)}
                    </div>
                    {isDeliveryTour && (
                      <div style={{ textAlign: "center" }}>
                        {config.start_time || "—"}
                      </div>
                    )}
                    {isDeliveryTour && (
                      <div style={{ textAlign: "center" }}>
                        {config.time_constraint_mode === "soft"
                          ? t("Soft")
                          : config.time_constraint_mode === "hard"
                            ? t("Hard")
                            : t("Off")}
                      </div>
                    )}
                    {isDeliveryTour && (
                      <div style={{ textAlign: "center" }}>
                        {config.num_vehicles ?? 1}
                      </div>
                    )}
                    <div style={{ textAlign: "center" }}>
                      {config.scanning_codes_enabled ? <CheckIcon /> : <XIcon />}
                    </div>
                    <div style={{ textAlign: "center" }}>
                      {config.allow_manual_orders_marking ? <CheckIcon /> : <XIcon />}
                    </div>
                    <div style={{ textAlign: "center" }}>
                      {config.bypass_route_start_verification ? <CheckIcon /> : <XIcon />}
                    </div>
                  </div>
                ))}
            </div>
          </>
        ) : null}

        <Typography className={classes._infoheading} variant="h5">
          {t("Route Details")}
        </Typography>

        {showPickupDetails && (
          pickupRoutes
            .sort((a, b) => sortOrder.indexOf(a.delivery_timing) - sortOrder.indexOf(b.delivery_timing))
            .map((route) => (
              <React.Fragment key={route.delivery_timing}>
                <Typography variant="h6" className={classes._timingHeading}>
                  {t("Route Setup for")} {t(route.delivery_timing)}
                </Typography>
                <div className={classes._basicdetailhead}>
                  <div>
                    <Typography className={classes._head} variant="h6">
                      {t("Pickup Start")}
                    </Typography>
                    <Typography className={classes._detail} variant="h6">
                      {getSupplierLocationName(route.pickup_start)}
                    </Typography>
                  </div>
                  <div>
                    <Typography className={classes._head} variant="h6">
                      {pickupEndLabel}
                    </Typography>
                    <Typography className={classes._detail} variant="h6">
                      {getSupplierLocationName(route.pickup_end)}
                    </Typography>
                  </div>
                  {route.stops?.length > 0 && (
                    <div>
                      <Typography className={classes._head} variant="h6">
                        {t("Stops")}
                      </Typography>
                      <Typography className={classes._detail} variant="h6">
                        {route.stops.map((stopId) => getSupplierLocationName(stopId)).join(", ")}
                      </Typography>
                    </div>
                  )}
                </div>
                {user?.permissions?.tours?.extended_features && (
                  <Box className={classes._routeVisualizationBox}>
                    <TourRouteVisualization
                      start={getSupplierLocationName(route.pickup_start) || "Start"}
                      customerStops={route.stops?.map((stopId) => getSupplierLocationName(stopId)).filter(Boolean) || []}
                      customerStopsLabel="Pickup Stops"
                      end={getSupplierLocationName(route.pickup_end) || "End"}
                      stopsBeforeEnd={[]}
                      stopsAfterEnd={[]}
                    />
                  </Box>
                )}
              </React.Fragment>
            ))
        )}

        {!isPickupOnlyTour && (
          tour?.custom_route_enabled && tour?.delivery_timing_configs ? (
            [...tour.delivery_timing_configs]
              .sort((a, b) => sortOrder.indexOf(a.delivery_timing) - sortOrder.indexOf(b.delivery_timing))
              .map((config) => (
                <React.Fragment key={config.delivery_timing}>
                  <Typography variant="h6" className={classes._timingHeading}>
                    {t('Route Setup for')} {t(config.delivery_timing)}
                  </Typography>

                  <div className={classes._basicdetailhead}>
                    {user?.permissions?.tours?.extended_features && (
                      <>
                        <div>
                          <Typography className={classes._head} variant="h6">
                            {t("Tour Type")}
                          </Typography>
                          <Typography className={classes._detail} variant="h6">
                            {t(
                              config.tour_type === "one_way"
                                ? "One Way"
                                : config.tour_type === "return_to_origin"
                                  ? "Return to Origin"
                                  : "Custom"
                            )}
                          </Typography>
                        </div>
                        <div>
                          <Typography className={classes._head} variant="h6">
                            {t("Efficient Routing Scheme")}
                          </Typography>
                          <Typography className={classes._detail} variant="h6">
                            {t(config.route_type === "distances" ? "Shortest Distance Mapbox" : config.route_type === "durations" ? "Shortest Duration Mapbox" : config.route_type === "distances_google" ? "Shortest Distance Google" : config.route_type || "Distance")}
                          </Typography>
                        </div>
                      </>
                    )}
                    <div>
                      <Typography className={classes._head} variant="h6">
                        {deliveryStartLabel}
                      </Typography>
                      <Typography className={classes._detail} variant="h6">
                        {supplierLocations?.find((location) => config.supplier_location_id === location.id)?.name}
                      </Typography>
                    </div>
                    {user?.permissions?.tours?.extended_features && config.tour_type !== "one_way" && (
                      <div>
                        <Typography className={classes._head} variant="h6">
                          {t("Tour End Location")}
                        </Typography>
                        <Typography className={classes._detail} variant="h6">
                          {getEndPointLocationForTiming(config)}
                        </Typography>
                      </div>
                    )}
                  </div>

                  {(user?.permissions?.tours?.extended_features &&
                    config.tour_type !== "one_way" &&
                    (config.fix_stops?.some((s) => s.type === "before_end") || config.fix_stops?.some((s) => s.type === "after_end"))) && (
                      <div className={classes._basicdetailhead}>
                        {config.fix_stops?.some((s) => s.type === "before_end") && (
                          <div>
                            <Typography className={classes._head} variant="h6">
                              {t("Stops Before Tour End")}
                            </Typography>
                            <Typography className={classes._detail} variant="h6">
                              {getStopsBeforeEndForTiming(config).join(", ")}
                            </Typography>
                          </div>
                        )}
                        {config.fix_stops?.some((s) => s.type === "after_end") && (
                          <div>
                            <Typography className={classes._head} variant="h6">
                              {t("Stops After Tour End")}
                            </Typography>
                            <Typography className={classes._detail} variant="h6">
                              {getStopsAfterEndForTiming(config).join(", ")}
                            </Typography>
                          </div>
                        )}
                      </div>
                    )}

                  {user?.permissions?.tours?.extended_features && (
                    <Box className={classes._routeVisualizationBox}>
                      <TourRouteVisualization
                        start={
                          supplierLocations?.find((o) => o.id === config.supplier_location_id)?.name ||
                          "Start Location"
                        }
                        customerStops={tour?.name ? [tour.name] : []}
                        end={config.tour_type === "one_way" ? null : getEndPointLocationForTiming(config)}
                        stopsBeforeEnd={getStopsBeforeEndForTiming(config)}
                        stopsAfterEnd={getStopsAfterEndForTiming(config)}
                      />
                    </Box>
                  )}
                </React.Fragment>
              ))
          ) : (
            // Standard single route
            <>
              <div className={classes._basicdetailhead}>
                {user?.permissions?.tours?.extended_features && (
                  <>
                    <div>
                      <Typography className={classes._head} variant="h6">
                        {t("Tour Type")}
                      </Typography>
                      <Typography className={classes._detail} variant="h6">
                        {t(
                          tour?.tour_type === "one_way"
                            ? "One Way"
                            : tour?.tour_type === "return_to_origin"
                              ? "Return to Origin"
                              : "Custom"
                        )}
                      </Typography>
                    </div>
                    <div>
                      <Typography className={classes._head} variant="h6">
                        {t("Efficient Routing Scheme")}
                      </Typography>
                      <Typography className={classes._detail} variant="h6">
                        {t(tour?.route_type === "distances" ? "Shortest Distance Mapbox" : tour?.route_type === "durations" ? "Shortest Duration Mapbox" : tour?.route_type === "distances_google" ? "Shortest Distance Google" : tour?.route_type || "Distance")}
                      </Typography>
                    </div>
                  </>
                )}
                <div>
                  <Typography className={classes._head} variant="h6">
                    {deliveryStartLabel}
                  </Typography>
                  <Typography className={classes._detail} variant="h6">
                    {supplierLocations?.find((location) => tour?.supplier_location_id === location.id)?.name}
                  </Typography>
                </div>
                {user?.permissions?.tours?.extended_features && tour?.tour_type !== "one_way" && (
                  <div>
                    <Typography className={classes._head} variant="h6">
                      {t("Tour End Location")}
                    </Typography>
                    <Typography className={classes._detail} variant="h6">
                      {getEndPointLocation()}
                    </Typography>
                  </div>
                )}
              </div>
              <div className={classes._basicdetailhead}>
                {user?.permissions?.tours?.extended_features &&
                  tour?.tour_type !== "one_way" &&
                  tour?.stops?.some((s) => s.type === "before_end") && (
                    <div>
                      <Typography className={classes._head} variant="h6">
                        {t("Stops Before Tour End")}
                      </Typography>
                      <Typography className={classes._detail} variant="h6">
                        {getStopsBeforeEnd().join(", ")}
                      </Typography>
                    </div>
                  )}
                {user?.permissions?.tours?.extended_features &&
                  tour?.tour_type !== "one_way" &&
                  tour?.stops?.some((s) => s.type === "after_end") && (
                    <div>
                      <Typography className={classes._head} variant="h6">
                        {t("Stops After Tour End")}
                      </Typography>
                      <Typography className={classes._detail} variant="h6">
                        {getStopsAfterEnd().join(", ")}
                      </Typography>
                    </div>
                  )}
              </div>

              <Box mt={8} >
                {user?.permissions?.tours?.extended_features && (
                  <TourRouteVisualization
                    start={supplierLocations?.find((o) => o.id === tour?.supplier_location_id)?.name || "Start Location"}
                    customerStops={tour?.name ? [tour.name] : []}
                    end={tour?.tour_type === "one_way" ? null : getEndPointLocation()}
                    stopsBeforeEnd={getStopsBeforeEnd()}
                    stopsAfterEnd={getStopsAfterEnd()}
                  />
                )}
              </Box>
            </>
          )
        )}
      </DarkLayout>
    </>
  );
};

export default TourDetail;
